import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Label, LabelSuffix } from '@swyftx/aviary/atoms/Label';
import { Select, SelectItemData } from '@swyftx/aviary/atoms/Select';
import { Body } from '@swyftx/aviary/atoms/Typography';

import { AccountPurpose, InvestmentPlans, SourceOfWealth } from '@shared/enums';

import { useOnboardingSelector, useOnboardingService } from '@Onboarding/Onboarding.context';
import { VerificationData } from '@Onboarding/types/Onboarding.types';

import { Controller, useForm } from 'react-hook-form';

import { OnboardingStepContainer } from './OnboardingStepContainer';

type FormInputs = Pick<VerificationData, 'sourceOfWealth' | 'accountPurpose' | 'investmentPlans'>;

const investmentPlansList: SelectItemData<InvestmentPlans>[] = [
  {
    label: '$1 - $25,000',
    value: '25000',
  },
  {
    label: '$25,000 - $50,000',
    value: '50000',
  },
  {
    label: '$50,001 - $100,000',
    value: '100000',
  },
  {
    label: '$100,001+',
    value: '100000more',
  },
];

export const InvestingPlansStep: React.FC = () => {
  const { t } = useTranslation('onboarding');
  const onboardingService = useOnboardingService();
  const verificationData = useOnboardingSelector((state) => state.context.verificationData);

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<FormInputs>({
    mode: 'onChange',
    defaultValues: {
      sourceOfWealth: verificationData?.sourceOfWealth,
      accountPurpose: verificationData?.accountPurpose,
      investmentPlans: verificationData?.investmentPlans,
    },
  });

  const onSubmit = (data: FormInputs) => {
    onboardingService.send({ type: 'NEXT', data });
  };

  const sourceOfWealthList = useMemo(
    () =>
      Object.values(SourceOfWealth).map((source) => ({
        label: t(`fields.sourceOfWealth.options.${source}`),
        value: source,
      })),
    [t],
  );

  const accountPurposeList = useMemo(
    () =>
      Object.values(AccountPurpose).map((purpose) => ({
        label: t(`fields.accountPurpose.options.${purpose}`),
        value: purpose,
      })),
    [t],
  );

  return (
    <OnboardingStepContainer onSubmit={handleSubmit(onSubmit)} submitDisabled={!isValid}>
      <main className='grid gap-32'>
        <Body size='medium' color='secondary' className='mb-32'>
          {t('steps.setupProfile.investingPlans.description')}{' '}
        </Body>

        <Controller
          name='sourceOfWealth'
          control={control}
          rules={{
            required: t('fields.sourceOfWealth.required'),
          }}
          render={({ field }) => (
            <section className='grid gap-8'>
              <Label>{t('fields.sourceOfWealth.label')}</Label>
              <Select
                placeholder='Select'
                items={sourceOfWealthList}
                value={field.value}
                onValueChange={(value) => {
                  field.onChange(value);
                }}
                error={!!errors.sourceOfWealth?.message}
                required
              />
            </section>
          )}
        />

        <Controller
          name='accountPurpose'
          control={control}
          rules={{
            required: t('fields.accountPurpose.required'),
          }}
          render={({ field }) => (
            <section className='grid gap-8'>
              <Label>{t('fields.accountPurpose.label')}</Label>
              <Select
                placeholder='Select'
                items={accountPurposeList}
                value={field.value}
                onValueChange={(value) => {
                  field.onChange(value);
                }}
                error={!!errors.accountPurpose?.message}
                required
              />
            </section>
          )}
        />

        <Controller
          name='investmentPlans'
          control={control}
          render={({ field }) => (
            <section className='grid gap-8'>
              <LabelSuffix>
                <Label>{t('fields.investingPlans.label')}</Label>
              </LabelSuffix>
              <Select
                placeholder='Select'
                items={investmentPlansList}
                value={field.value}
                onValueChange={(value) => {
                  field.onChange(value);
                }}
                error={!!errors.accountPurpose?.message}
              />
            </section>
          )}
        />
      </main>
    </OnboardingStepContainer>
  );
};
